
import { useState, useCallback, useRef, useEffect } from 'react';
import { MicrophoneLevelMeter } from './MicrophoneLevelMeter';
import { useMicrophoneLevel } from '@/hooks/useMicrophoneLevel';

const DEFAULT_MAX_SECONDS = 60;

function getMediaNotAvailableMessage(): string | null {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return 'Microphone is not available. Use HTTPS or open this page from the same device (localhost).';
  }
  return null;
}

export interface AudioRecorderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (blob: Blob, durationSeconds: number) => void;
  displayName: string;
  prayerBody: string;
  maxSeconds?: number;
}

type RecorderState = 'preview' | 'recording' | 'playback';

export function AudioRecorderModal({
  isOpen,
  onClose,
  onComplete,
  displayName,
  prayerBody,
  maxSeconds = DEFAULT_MAX_SECONDS,
}: AudioRecorderModalProps) {
  const [state, setState] = useState<RecorderState>('preview');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [micError, setMicError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playbackAudioRef = useRef<HTMLAudioElement | null>(null);

  const stopAllTracks = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, [stream]);

  const stopRecording = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setState('playback');
  }, []);

  const startRecording = useCallback(async () => {
    const notAvail = getMediaNotAvailableMessage();
    if (notAvail) {
      setMicError(notAvail);
      return;
    }
    if (!stream) {
      setMicError('Microphone not ready.');
      return;
    }
    try {
      setMicError(null);
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : '';
      const options: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      const blobType = recorder.mimeType || 'audio/webm';
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        if (chunksRef.current.length) {
          const blob = new Blob(chunksRef.current, { type: blobType });
          setRecordedBlob(blob);
          setPlaybackUrl(URL.createObjectURL(blob));
        }
      };
      recorder.start(1000);
      setState('recording');
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => {
          if (s >= maxSeconds - 1) {
            if (recordingTimerRef.current) {
              clearInterval(recordingTimerRef.current);
              recordingTimerRef.current = null;
            }
            stopRecording();
            return maxSeconds;
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      setMicError('Could not access microphone.');
    }
  }, [maxSeconds, stream, stopRecording]);

  useEffect(() => {
    if (!isOpen) return;
    let s: MediaStream | null = null;
    const acquire = async () => {
      const notAvail = getMediaNotAvailableMessage();
      if (notAvail) {
        setMicError(notAvail);
        return;
      }
      try {
        setMicError(null);
        s = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        setStream(s);
      } catch {
        setMicError('Could not access microphone. Please allow microphone access and try again.');
      }
    };
    acquire();
    return () => {
      if (s) s.getTracks().forEach((t) => t.stop());
      setStream(null);
    };
  }, [isOpen]);

  useEffect(() => {
    if (state !== 'playback' || !playbackUrl || !playbackAudioRef.current) return;
    const el = playbackAudioRef.current;
    el.src = playbackUrl;
    el.play().catch(() => {});
  }, [state, playbackUrl]);

  const handleClose = useCallback(() => {
    const urlToRevoke = playbackUrl;
    stopAllTracks();
    setState('preview');
    setRecordedBlob(null);
    setPlaybackUrl(null);
    setRecordingSeconds(0);
    setMicError(null);
    onClose();
    if (urlToRevoke) {
      setTimeout(() => URL.revokeObjectURL(urlToRevoke), 100);
    }
  }, [playbackUrl, stopAllTracks, onClose]);

  const handleReRecord = useCallback(() => {
    const urlToRevoke = playbackUrl;
    setPlaybackUrl(null);
    setRecordedBlob(null);
    setRecordingSeconds(0);
    setState('preview');
    if (urlToRevoke) {
      setTimeout(() => URL.revokeObjectURL(urlToRevoke), 100);
    }
  }, [playbackUrl]);

  const handleContinue = useCallback(() => {
    if (recordedBlob) {
      onComplete(recordedBlob, recordingSeconds);
      handleClose();
    }
  }, [recordedBlob, recordingSeconds, onComplete, handleClose]);

  useEffect(() => {
    if (!isOpen) {
      const urlToRevoke = playbackUrl;
      setState('preview');
      setRecordedBlob(null);
      setPlaybackUrl(null);
      setRecordingSeconds(0);
      if (urlToRevoke) {
        setTimeout(() => URL.revokeObjectURL(urlToRevoke), 100);
      }
    }
  }, [isOpen, playbackUrl]);

  const micLevel = useMicrophoneLevel(stream, isOpen && state !== 'playback' && !micError);

  if (!isOpen) return null;

  const isPreview = state === 'preview';
  const isRecording = state === 'recording';
  const isPlayback = state === 'playback';

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex flex-col items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Record audio response"
    >
      {isPreview && (
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 left-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-900 shadow-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <p className="text-sm font-medium text-slate-900 dark:text-white">{displayName}</p>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-3">{prayerBody}</p>
        </div>

        <div className="p-6 flex flex-col items-center gap-4 min-h-[200px] justify-center">
          {micError && isPreview && (
            <div className="text-center space-y-3">
              <p className="text-sm text-amber-600 dark:text-amber-400">{micError}</p>
              <button
                type="button"
                onClick={() => {
                  setMicError(null);
                  navigator.mediaDevices
                    ?.getUserMedia({ audio: true, video: false })
                    .then((s) => setStream(s))
                    .catch(() => setMicError('Could not access microphone.'));
                }}
                className="px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm font-medium"
              >
                Retry microphone
              </button>
            </div>
          )}

          {!micError && (isPreview || isRecording) && (
            <>
              <MicrophoneLevelMeter level={micLevel} />

              {isRecording ? (
                <div className="flex items-center gap-2 text-red-600 font-medium">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500 animate-pulse" />
                  {recordingSeconds}s / {maxSeconds}s
                </div>
              ) : (
                <p className="text-sm text-slate-600 dark:text-slate-400">Tap to start recording (max {maxSeconds}s)</p>
              )}
              <div>
                {isPreview ? (
                  <button
                    type="button"
                    onClick={startRecording}
                    aria-label="Start recording"
                    className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 shadow-lg ring-4 ring-red-200 dark:ring-red-900/50"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={stopRecording}
                    aria-label="Stop recording"
                    className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 shadow-lg ring-4 ring-red-200 dark:ring-red-900/50 flex items-center justify-center"
                  >
                    <span className="w-6 h-6 rounded-sm bg-white" />
                  </button>
                )}
              </div>
            </>
          )}

          {isPlayback && playbackUrl && (
            <div className="w-full space-y-4">
              <audio ref={playbackAudioRef} controls className="w-full" />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={handleReRecord}
                  className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-medium"
                >
                  Record Again
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
